import { Core } from "@faire/design-tokens";
import { trackProductInfoValuePropsView } from "@faire/web-api/events/productInfo/view/valueProps";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Calendar60 } from "@faire/web/slate/icons/CalendarDays";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import CuratedSelectionIcon from "@faire/retailer/packages/core-icons/values/Collections.svg";
import FreeReturnsIcon from "@faire/retailer/packages/core-icons/values/FreeReturn.svg";

const ProductPerksCardBase: React.FC = () => {
  const ref = useDoActionWhenVisible(trackProductInfoValuePropsView);

  return (
    <Wrapper ref={ref}>
      <Typography variant="subheadingSansRegular" align="center">
        <StrictLocalMsg defaultMessage="Stock your store with Faire" />
      </Typography>
      <Spacer height="4x" />
      <Perks />
    </Wrapper>
  );
};

const PerksBase: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();

  const perks = [
    {
      id: 1,
      icon: (
        <img
          src={CuratedSelectionIcon}
          alt={strictLocalize({
            defaultMessage: "Curated selection of items",
            description: {
              text: "Alt text of an icon of items",
            },
          })}
          height="20"
          width="20"
        />
      ),
      text: strictLocalize({
        defaultMessage: "Find unique items from independent brands",
      }),
    },
    {
      id: 2,
      icon: (
        <Calendar60
          height="20px"
          width="20px"
          titleAccess={strictLocalize({
            defaultMessage: "Calendar with 60 days",
            description: {
              text: "Alt text of an icon of a calendar",
            },
          })}
        />
      ),
      text: strictLocalize({
        defaultMessage: "Buy now, pay later with 60-day payment terms",
        description: {
          text: "For retailers | Desktop and mWeb | Displays on logged-out product details page | 1 of 3 value props | Preceded by this header: Stock your store with Faire",
          img: "https://cdn.faire.com/fastly/eaa780cfa0c237af25a939cfd94c753ed560f0af137d4327755134f4f256efed.png",
        },
      }),
    },
    {
      id: 3,
      icon: (
        <img
          src={FreeReturnsIcon}
          alt={strictLocalize({
            defaultMessage: "Free returns box",
            description: {
              text: "Alt text of an icon of a free returns box",
            },
          })}
          height="20"
          width="20"
        />
      ),
      text: strictLocalize({
        defaultMessage: "Free returns for risk-free buying",
        description: {
          text: "One of the benefits Faire offers. Retailers get free returns to reduce risk when buying new products.",
        },
      }),
    },
  ];

  return (
    <Column gap="3x">
      {perks.map((perk) => (
        <PerkRow key={perk.id}>
          <Icon>{perk.icon}</Icon>
          <Column flex={1}>
            <Typography>{perk.text}</Typography>
          </Column>
        </PerkRow>
      ))}
    </Column>
  );
};

const Perks = observer(PerksBase);

export const ProductPerksCard = observer(ProductPerksCardBase);

const Wrapper = styled(Column).attrs({
  justify: "flex-start",
  align: "center",
})`
  width: 100%;
  background-color: ${Core.surface.muted};
  padding: ${getSpacing("4x")};
`;

const PerkRow = styled(Row).attrs({ align: "center", justify: "center" })`
  width: 100%;
`;

const Icon = styled(Column).attrs({ align: "center", justify: "center" })`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${Core.surface.inverse};
  margin-right: 20px;
`;
